import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useStore, guardStats, fmtIDR, fmtDate } from '../lib/store';
import { BackBar, Money, inputCls } from '../components/Bits';

export default function Ringkasan() {
  const { state, setLesson } = useStore();
  const [note, setNote] = useState(state.lesson || '');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const paid = state.payments.filter((p) => p.status === 'paid');
  const upcomingAll = state.payments.filter((p) => p.status === 'upcoming');
  const reviewedTotal =
    paid.reduce((a, p) => a + p.amount, 0) +
    upcomingAll.filter((p) => guardStats(state, p.id).remaining === 0).reduce((a, p) => a + p.amount, 0);
  const vendorValue = state.vendors.reduce((a, v) => a + (v.value || 0), 0);

  const saveLesson = () => {
    setLesson(note.trim());
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const copySummary = async () => {
    const lines = [
      'Catatan dari perjalanan wedding kami',
      `${state.vendors.length} vendor tercatat · ${fmtIDR(reviewedTotal)} telah ditinjau sebelum dibayar.`,
    ];
    if ((state.lesson || note).trim()) lines.push(`Yang kami pelajari: ${(state.lesson || note).trim()}`);
    lines.push('Dibantu dengan SebelumSah — Cek dulu sebelum bayar.');
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
    } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div data-testid="ringkasan-page" className="space-y-6">
      <BackBar to="/app" title="Hari Ini" />

      <div className="relative overflow-hidden rounded-[20px] border border-line bg-white soft-shadow p-6">
        <div className="kawung absolute inset-0 opacity-[0.03] pointer-events-none" />
        <div className="relative">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra mb-2">Ringkasan Pernikahan</p>
          <h1 className="font-serif text-2xl font-medium tracking-tight text-ink">{state.profile.coupleName}</h1>
          <p className="font-mono tnum text-[11px] text-cashmere mt-1">{fmtDate(state.profile.weddingDate)}</p>

          <div className="mt-5 space-y-3 border-t border-line pt-5">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-cashmere">Vendor tercatat</p>
              <p data-testid="ringkasan-vendors" className="font-mono tnum text-sm font-semibold text-ink">{state.vendors.length}</p>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-cashmere">Total nilai kerja sama</p>
              <Money data-testid="ringkasan-value" className="text-sm font-semibold text-ink">{fmtIDR(vendorValue)}</Money>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-cashmere">Pembayaran tercatat selesai</p>
              <p data-testid="ringkasan-paid" className="font-mono tnum text-sm font-semibold text-ink">{paid.length}</p>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-cashmere">Telah ditinjau sebelum dibayar</p>
              <Money data-testid="ringkasan-reviewed" className="text-sm font-semibold text-sage">{fmtIDR(reviewedTotal)}</Money>
            </div>
          </div>

          <p className="mt-5 text-xs text-cashmere leading-relaxed border-t border-line pt-4">
            Catatan perjalanan ini tetap tersimpan jika suatu hari kalian ingin melihatnya kembali.
          </p>
        </div>
      </div>

      <div className="rounded-[20px] border border-line bg-white soft-shadow p-5">
        <p className="font-serif text-lg font-medium text-ink">Satu hal yang kami pelajari</p>
        <p className="mt-1 text-xs text-cashmere leading-relaxed">Apa hal penting tentang vendor atau pembayaran yang ingin kalian ingat?</p>
        <textarea
          data-testid="lesson-input"
          rows={3}
          maxLength={200}
          className={inputCls + ' mt-3 text-sm'}
          placeholder="Pastikan setiap perubahan paket dikonfirmasi secara tertulis sebelum pembayaran berikutnya."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="font-mono text-[10px] tnum text-cashmere/70">{note.length}/200</span>
          <button data-testid="lesson-save-btn" onClick={saveLesson} className="rounded-full bg-ink text-ivory px-4 py-2 text-[11px] font-bold hover:bg-inkdeep transition-colors">
            {saved ? 'Tersimpan rapi.' : 'Simpan Catatan'}
          </button>
        </div>
      </div>

      <button
        data-testid="copy-summary-btn"
        onClick={copySummary}
        className="w-full flex items-center justify-center gap-2 rounded-full border border-ink/20 bg-white py-3.5 text-sm font-bold text-ink hover:bg-bone transition-colors"
      >
        {copied ? (<><Check size={15} strokeWidth={3} className="text-sage" /> Tersalin</>) : (<><Copy size={15} /> Salin Ringkasan</>)}
      </button>
    </div>
  );
}
